import React, { useEffect, useMemo, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { TaskList, TaskItem } from '@tiptap/extension-list';
import { TableKit } from '@tiptap/extension-table';
import SlashCommand from './extensions/SlashCommand';
import MarkdownPaste from './extensions/MarkdownPaste';
import Callout from './extensions/Callout';
import TableOfContents from './extensions/TableOfContents';
import EntityMention from './extensions/EntityMention';
import ChildPage from './extensions/ChildPage';
import BlockExits from './extensions/BlockExits';
import ScriptKeymap, { ScriptNodes } from './extensions/ScriptElements';
import ScriptCharacterSuggestion from './extensions/ScriptCharacterSuggestion';
import { routeForEntity } from '../../services/entityMentionProviders';
import { SCRIPT_ELEMENTS } from '../../services/scriptFormat';
import {
	CAPABILITIES_PLAIN,
	CAPABILITIES_DOCS,
	CAPABILITIES_SCRIPT,
} from './editorCapabilities';
import './CyvEditor.css';
import './ScriptFormat.css';

// Re-exported so a consumer can import the component and its presets together.
// The presets themselves live in editorCapabilities.js (import-pure, so they
// stay unit-testable — this file pulls in TipTap and cannot be).
export { CAPABILITIES_PLAIN, CAPABILITIES_DOCS, CAPABILITIES_SCRIPT };

// Placeholder text per script element, so an empty line names what it is. Built
// once from the element catalogue rather than restated here.
const SCRIPT_PLACEHOLDERS = Object.fromEntries(
	SCRIPT_ELEMENTS.map((el) => [el.name, el.label])
);

/* CyvEditor — the shared rich-text/block editor primitive.
 *
 * Deliberately NOT a Docs-only widget. Docs v1 is its first mount, but it is
 * designed to become the one editor across the portal: Lesson Recap and Song
 * Notes fold in later by mounting it with a REDUCED capability set rather than
 * by forking the component (decided with Anthony 2026-07-23).
 *
 * Capabilities gate EXTENSIONS, not just UI. A capability that is off means the
 * node is never registered in the schema, so a reduced mount cannot produce a
 * block that a plainer surface would fail to render. That matters because the
 * family-visible surfaces (Recap tiers) must stay plain rich text — the "/"
 * menu filters itself against the same flags (see SlashCommand.filterSlashItems).
 *
 * The default is the PLAIN set on purpose: a new mount is conservative until it
 * opts in, so nobody accidentally ships slash commands to a parent-facing page.
 */

function CyvEditor({
	content = null,
	onChange,
	capabilities = CAPABILITIES_PLAIN,
	placeholder = 'Write something, or press “/” for blocks…',
	editable = true,
	autofocus = false,
	className = '',
	onEditorReady,
	onEntityClick,
	childPage,
}) {
	/* Child-page handlers travel through a ref whose IDENTITY never changes, so
	   a re-render with new callbacks cannot land in the extensions memo and
	   re-create the editor mid-edit (which would drop the user's cursor). */
	const childPageRef = useRef({});
	childPageRef.current = childPage || {};
	// Guards the round-trip: we echo our own emitted JSON back through the
	// `content` prop from the consumer's state, and re-applying it would reset
	// the cursor mid-typing. Compare against the last value WE emitted.
	const lastEmitted = useRef(null);

	const extensions = useMemo(() => {
		const list = [
			StarterKit.configure({
				heading: { levels: [1, 2, 3] },
			}),
			Placeholder.configure({
				placeholder: ({ node }) => {
					if (node.type.name === 'heading' && node.attrs.level === 1) return 'Untitled';
					// A script line names itself while empty — the Tab cycle is
					// otherwise invisible, and a bare indent tells you nothing.
					return SCRIPT_PLACEHOLDERS[node.type.name] || placeholder;
				},
			}),
		];

		if (capabilities.tasks) {
			list.push(TaskList, TaskItem.configure({ nested: true }));
		}
		if (capabilities.callout) list.push(Callout);
		if (capabilities.tables) {
			// Production paperwork (show flow, cue sheet, plots) is fundamentally
			// tabular — this is what makes the show-flow doc type possible.
			list.push(TableKit.configure({ table: { resizable: true } }));
		}
		if (capabilities.toc) list.push(TableOfContents);
		if (capabilities.slashCommands) {
			list.push(SlashCommand.configure({ capabilities }));
		}
		if (capabilities.mentions) list.push(EntityMention);
		if (capabilities.markdownPaste) list.push(MarkdownPaste);
		if (capabilities.childPages) {
			list.push(ChildPage.configure({ handlers: childPageRef }));
		}
		/* Script elements + their keymap. Registered BEFORE BlockExits for the
		   same reason BlockExits is last: the later extension wins the keymap,
		   and Shift-Enter must keep meaning "leave this block". */
		if (capabilities.script) {
			list.push(...ScriptNodes, ScriptCharacterSuggestion, ScriptKeymap);
		}
		// Last, so its Shift-Enter keymap wins over the container blocks'.
		list.push(BlockExits);

		return list;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		capabilities.tasks,
		capabilities.callout,
		capabilities.toc,
		capabilities.tables,
		capabilities.childPages,
		capabilities.slashCommands,
		capabilities.mentions,
		capabilities.markdownPaste,
		capabilities.script,
		placeholder,
	]);

	const editor = useEditor(
		{
			extensions,
			content: content || undefined,
			editable,
			/* Spellcheck is off unless asked for. These are planning documents and
			   scripts that go to crew and students, so the red squiggle earns its
			   place (Anthony, 2026-07-24). */
			editorProps: { attributes: { spellcheck: 'true' } },
			autofocus: autofocus ? 'start' : false,
			onUpdate: ({ editor: ed }) => {
				const json = ed.getJSON();
				lastEmitted.current = json;
				onChange?.({ json, text: ed.getText() });
			},
		},
		[extensions]
	);

	// Apply externally-loaded content (e.g. the doc arriving from Firestore)
	// without clobbering what the user is actively typing.
	useEffect(() => {
		if (!editor || !content) return;
		if (content === lastEmitted.current) return;
		if (JSON.stringify(content) === JSON.stringify(editor.getJSON())) return;
		editor.commands.setContent(content, { emitUpdate: false });
	}, [editor, content]);

	useEffect(() => {
		if (editor) editor.setEditable(editable);
	}, [editor, editable]);

	useEffect(() => {
		if (editor && onEditorReady) onEditorReady(editor);
	}, [editor, onEditorReady]);

	/* Chip navigation is DELEGATED rather than rendered as an <a>: inside a
	   contenteditable, a real anchor fights text selection and caret placement.
	   A chip with no destination (project / repertoire — no detail surface in v1)
	   is inert, and says so via data-no-route so the cursor doesn't lie. */
	const handleClick = (e) => {
		const chip = e.target.closest?.('[data-entity-mention]');
		if (!chip) return;
		const type = chip.getAttribute('data-entity-type');
		const id = chip.getAttribute('data-entity-id');
		const to = routeForEntity(type, id);
		if (!to) return;
		e.preventDefault();
		onEntityClick?.({ type, id, label: chip.getAttribute('data-entity-label'), to });
	};

	return (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
		<div
			className={`cyv-editor ${capabilities.script ? 'cyv-editor--script' : ''} ${className}`
				.replace(/\s+/g, ' ')
				.trim()}
			onClick={handleClick}>
			<EditorContent editor={editor} />
		</div>
	);
}

export default CyvEditor;
